import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  FileText, Download, Car, Users, Clock, Package, 
  CheckCircle2, ClipboardList, Printer, Eye, ImageIcon
} from "lucide-react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const PdfExportPage = () => {
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [companySettings, setCompanySettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [orderImages, setOrderImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Set order from URL parameter after data is loaded
  useEffect(() => {
    const orderIdFromUrl = searchParams.get('order');
    if (orderIdFromUrl && orders.length > 0) {
      const orderExists = orders.find(o => o.id === orderIdFromUrl);
      if (orderExists) {
        setSelectedOrderId(orderIdFromUrl);
      }
    }
  }, [searchParams, orders]);

  // Load images when order is selected
  useEffect(() => {
    if (selectedOrderId) {
      fetchOrderImages(selectedOrderId);
    } else {
      setOrderImages([]);
    }
  }, [selectedOrderId]);

  const fetchOrderImages = async (orderId) => {
    setLoadingImages(true);
    try {
      const response = await api.get(`/api/work-orders/${orderId}/images`);
      setOrderImages(response.data);
    } catch (err) {
      console.error("Error fetching images:", err);
      setOrderImages([]);
    } finally {
      setLoadingImages(false);
    }
  };

  const fetchData = async () => {
    try {
      const [ordersRes, customersRes, vehiclesRes, settingsRes] = await Promise.all([
        api.get("/api/work-orders"),
        api.get("/api/customers"),
        api.get("/api/vehicles"),
        api.get("/api/company-settings")
      ]);
      setOrders(ordersRes.data);
      setCustomers(customersRes.data);
      setVehicles(vehiclesRes.data);
      setCompanySettings(settingsRes.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const getSelectedOrder = () => {
    return orders.find(o => o.id === selectedOrderId);
  };

  const getCustomer = (customerId) => {
    return customers.find(c => c.id === customerId);
  };

  const getVehicle = (vehicleId) => {
    return vehicles.find(v => v.id === vehicleId);
  };

  const formatMinutes = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}min`;
  };

  const getStatusLabel = (status) => {
    const labels = {
      offen: "Offen",
      in_bearbeitung: "In Bearbeitung",
      erledigt: "Erledigt"
    };
    return labels[status] || status;
  };

  const generatePdf = async (order, includeImages = false) => {
    if (!order) return;

    setGenerating(true);
    
    try {
      const customer = getCustomer(order.customer_id);
      const vehicle = getVehicle(order.vehicle_id);
      
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPos = 20;

      // Company name from settings or default
      const companyName = companySettings?.company_name || "VyWork";
      const hasCompanyLogo = companySettings?.logo_url;
      const logoSize = companySettings?.logo_size || 50; // Size in mm

      // Header with company logo if available
      let textStartX = 14;
      if (hasCompanyLogo) {
        try {
          // Logo size from settings (default 50mm)
          doc.addImage(companySettings.logo_url, 'PNG', 14, yPos - 5, logoSize, logoSize);
          textStartX = 14 + logoSize + 5; // Position text after logo
        } catch (e) {
          console.log("Logo could not be added to PDF");
        }
      }

      // Company name - positioned after logo
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text(companyName, textStartX, yPos + 5);
      
      // Company contact info
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100);
      
      let contactY = yPos + 12;
      if (companySettings?.street || companySettings?.city) {
        const addr = [companySettings.street, `${companySettings.postal_code || ''} ${companySettings.city || ''}`.trim()].filter(Boolean).join(', ');
        doc.text(addr, textStartX, contactY);
        contactY += 4;
      }
      if (companySettings?.phone || companySettings?.email) {
        const contact = [companySettings.phone, companySettings.email].filter(Boolean).join(' | ');
        doc.text(contact, textStartX, contactY);
      }
      
      // Adjust yPos if logo is large
      if (hasCompanyLogo && logoSize > 30) {
        yPos = yPos + logoSize - 10;
      }
      
      // Order number (right side) — Arbeitskarte title
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0);
      doc.text("ARBEITSKARTE", pageWidth - 14, yPos - 5, { align: "right" });
      
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      const orderNumText = `Nr. ${order.auftragsnummer || order.id.slice(0, 8)}`;
      doc.text(orderNumText, pageWidth - 14, yPos + 3, { align: "right" });
      
      // Date
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100);
      const dateText = `Erstellt: ${new Date(order.created_at).toLocaleDateString("de-DE")}`;
      doc.text(dateText, pageWidth - 14, yPos + 7, { align: "right" });
      
      // Status badge
      doc.setFontSize(10);
      const statusText = `Status: ${getStatusLabel(order.status)}`;
      doc.text(statusText, pageWidth - 14, yPos + 14, { align: "right" });

      yPos += 30;

      // Divider line
      doc.setDrawColor(200);
      doc.line(14, yPos, pageWidth - 14, yPos);
      yPos += 10;

      // Customer Data Section
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0);
      doc.text("KUNDENDATEN", 14, yPos);
      yPos += 8;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      
      if (customer) {
        const customerLines = [
          `Name: ${customer.name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim()}`,
          `Telefon: ${customer.phone || '-'}`,
          `E-Mail: ${customer.email || '-'}`,
          `Adresse: ${[customer.street, `${customer.postal_code || ''} ${customer.city || ''}`.trim()].filter(Boolean).join(', ') || '-'}`
        ];
        
        customerLines.forEach(line => {
          doc.text(line, 14, yPos);
          yPos += 6;
        });
      }

      yPos += 5;

      // Vehicle Data Section (or Service Object)
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(vehicle ? "FAHRZEUGDATEN" : "OBJEKT / DIENSTLEISTUNG", 14, yPos);
      yPos += 8;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      
      if (vehicle) {
        const vehicleLines = [
          `Kennzeichen: ${vehicle.license_plate || '-'}`,
          `Fahrzeug: ${vehicle.brand || ''} ${vehicle.model || ''}`.trim() || '-',
          `Baujahr: ${vehicle.year || '-'}`,
          `Fahrgestellnummer (VIN): ${vehicle.vin || '-'}`,
          `Kilometerstand: ${vehicle.mileage ? vehicle.mileage.toLocaleString('de-DE') + ' km' : '-'}`
        ];
        
        vehicleLines.forEach(line => {
          doc.text(line, 14, yPos);
          yPos += 6;
        });
      } else if (order.service_object) {
        doc.text(`Bezeichnung: ${order.service_object}`, 14, yPos);
        yPos += 6;
        doc.text("(Auftrag ohne Fahrzeugbezug — Dienstleistung / Vormontage)", 14, yPos);
        yPos += 6;
      }

      yPos += 5;

      // Order Description
      if (order.description) {
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text("AUFTRAGSBESCHREIBUNG", 14, yPos);
        yPos += 8;
        
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        const splitDescription = doc.splitTextToSize(order.description, pageWidth - 28);
        doc.text(splitDescription, 14, yPos);
        yPos += splitDescription.length * 5 + 5;
      }

      yPos += 5;

      // Tasks Section
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("DURCHGEFÜHRTE ARBEITEN", 14, yPos);
      yPos += 8;

      if (order.tasks && order.tasks.length > 0) {
        const taskData = order.tasks.map((task, index) => {
          // Format materials as multi-line string (each on new line)
          let materials = '-';
          if (task.materialien && task.materialien.length > 0) {
            materials = task.materialien.map(m => {
              let line = `• ${m.bezeichnung}`;
              // Support both new format (anzahl + einheit) and old format (menge)
              if (m.anzahl && m.einheit) {
                line += ` (${m.anzahl} ${m.einheit})`;
              } else if (m.anzahl) {
                line += ` (${m.anzahl})`;
              } else if (m.menge) {
                line += ` (${m.menge})`;
              }
              if (m.artikelnummer) line += ` [${m.artikelnummer}]`;
              if (m.kosten > 0) line += ` - ${m.kosten.toFixed(2)}€`;
              return line;
            }).join('\n');
          } else if (task.material) {
            materials = task.material;
          }
          
          const totalCost = task.materialien && task.materialien.length > 0
            ? task.materialien.reduce((sum, m) => sum + (m.kosten || 0), 0)
            : (task.material_cost || 0);

          return [
            (index + 1).toString(),
            task.description,
            task.completed ? "✓ Erledigt" : "○ Offen",
            materials,
            totalCost > 0 ? `${totalCost.toFixed(2)} €` : '-',
            task.time_minutes > 0 ? `${task.time_minutes} min` : '-'
          ];
        });

        autoTable(doc, {
          startY: yPos,
          head: [['Nr.', 'Aufgabe', 'Status', 'Material', 'Kosten', 'Zeit']],
          body: taskData,
          theme: 'striped',
          headStyles: { 
            fillColor: [59, 130, 246],
            fontSize: 9,
            fontStyle: 'bold'
          },
          bodyStyles: { 
            fontSize: 8,
            cellPadding: 3
          },
          columnStyles: {
            0: { cellWidth: 10 },
            1: { cellWidth: 40 },
            2: { cellWidth: 20 },
            3: { cellWidth: 60 },  // Wider for multi-line materials
            4: { cellWidth: 18 },
            5: { cellWidth: 18 }
          },
          margin: { left: 14, right: 14 },
          styles: {
            overflow: 'linebreak',
            cellWidth: 'wrap'
          }
        });

        yPos = doc.lastAutoTable.finalY + 10;
      } else {
        doc.setFontSize(10);
        doc.setFont("helvetica", "italic");
        doc.text("Keine Aufgaben erfasst", 14, yPos);
        yPos += 10;
      }

      // Time Tracking Section
      if (order.time_entries && order.time_entries.length > 0) {
        // Check if we need a new page
        if (yPos > 230) {
          doc.addPage();
          yPos = 20;
        }

        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0);
        doc.text("ZEITERFASSUNG", 14, yPos);
        yPos += 8;

        const timeData = order.time_entries
          .filter(e => !e.is_active && e.end_time)
          .map(entry => {
            const startParts = entry.start_time?.split(":") || [0, 0];
            const endParts = entry.end_time?.split(":") || [0, 0];
            const startMins = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
            const endMins = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
            const workMins = Math.max(0, endMins - startMins - (entry.pause_minutes || 0));

            return [
              entry.date || '-',
              entry.start_time || '-',
              entry.end_time || '-',
              entry.pause_minutes > 0 ? `${entry.pause_minutes} min` : '-',
              formatMinutes(workMins)
            ];
          });

        if (timeData.length > 0) {
          autoTable(doc, {
            startY: yPos,
            head: [['Datum', 'Start', 'Ende', 'Pause', 'Arbeitszeit']],
            body: timeData,
            theme: 'striped',
            headStyles: { 
              fillColor: [34, 197, 94],
              fontSize: 9,
              fontStyle: 'bold'
            },
            bodyStyles: { fontSize: 9 },
            margin: { left: 14, right: 14 }
          });

          yPos = doc.lastAutoTable.finalY + 8;

          // Total time
          doc.setFontSize(11);
          doc.setFont("helvetica", "bold");
          doc.text(`Gesamtarbeitszeit: ${formatMinutes(order.total_work_minutes || 0)}`, 14, yPos);
          yPos += 10;
        }
      }

      // Summary Section
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      // Calculate totals
      const totalMaterialCost = (order.tasks || []).reduce((sum, task) => {
        if (task.materialien && task.materialien.length > 0) {
          return sum + task.materialien.reduce((mSum, m) => mSum + (m.kosten || 0), 0);
        }
        return sum + (task.material_cost || 0);
      }, 0);

      const totalTaskTime = (order.tasks || []).reduce((sum, t) => sum + (t.time_minutes || 0), 0);
      const completedTasks = (order.tasks || []).filter(t => t.completed).length;
      const totalTasks = (order.tasks || []).length;

      // Labor cost (gestempelte Zeit * Stundensatz)
      const TAX_RATE = parseFloat(companySettings?.tax_rate != null ? companySettings.tax_rate : 19);
      const stundenlohn = parseFloat(companySettings?.stundenlohn || 0);
      const workedMinutes = parseInt(order.total_work_minutes || 0);
      const workedHours = workedMinutes / 60;
      const laborNet = workedHours * stundenlohn;
      const laborTax = (laborNet * TAX_RATE) / 100;
      const laborGross = laborNet + laborTax;

      const materialTax = (totalMaterialCost * TAX_RATE) / 100;
      const materialGross = totalMaterialCost + materialTax;

      const netSum = totalMaterialCost + laborNet;
      const taxSum = (netSum * TAX_RATE) / 100;
      const grossSum = netSum + taxSum;

      doc.setDrawColor(200);
      doc.line(14, yPos, pageWidth - 14, yPos);
      yPos += 10;

      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("ZUSAMMENFASSUNG", 14, yPos);
      yPos += 8;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");

      const summaryLines = [
        `Aufgaben erledigt: ${completedTasks} von ${totalTasks}`,
        `Geschätzte Arbeitszeit: ${formatMinutes(totalTaskTime)}`,
        `Erfasste Arbeitszeit: ${formatMinutes(workedMinutes)}`,
      ];

      summaryLines.forEach(line => {
        doc.text(line, 14, yPos);
        yPos += 6;
      });

      yPos += 2;

      // Cost breakdown table: Material, Arbeit, Gesamt (netto, MwSt, brutto)
      const breakdownBody = [];
      if (totalMaterialCost > 0) {
        breakdownBody.push([
          'Material',
          `${totalMaterialCost.toFixed(2)} €`,
          `${materialTax.toFixed(2)} €`,
          `${materialGross.toFixed(2)} €`,
        ]);
      }
      if (laborNet > 0) {
        breakdownBody.push([
          `Arbeit (${formatMinutes(workedMinutes)} × ${stundenlohn.toFixed(2)} €/h)`,
          `${laborNet.toFixed(2)} €`,
          `${laborTax.toFixed(2)} €`,
          `${laborGross.toFixed(2)} €`,
        ]);
      }

      if (breakdownBody.length > 0) {
        autoTable(doc, {
          startY: yPos,
          head: [['Position', 'Netto', `MwSt (${TAX_RATE}%)`, 'Brutto']],
          body: breakdownBody,
          foot: [[
            'Gesamt',
            `${netSum.toFixed(2)} €`,
            `${taxSum.toFixed(2)} €`,
            `${grossSum.toFixed(2)} €`,
          ]],
          theme: 'striped',
          headStyles: {
            fillColor: [59, 130, 246],
            fontSize: 9,
            fontStyle: 'bold',
          },
          footStyles: {
            fillColor: [226, 232, 240],
            textColor: 0,
            fontStyle: 'bold',
            fontSize: 10,
          },
          bodyStyles: { fontSize: 9 },
          columnStyles: {
            0: { cellWidth: 'auto' },
            1: { halign: 'right', cellWidth: 30 },
            2: { halign: 'right', cellWidth: 30 },
            3: { halign: 'right', cellWidth: 32 },
          },
          margin: { left: 14, right: 14 },
        });
        yPos = doc.lastAutoTable.finalY + 6;
      } else {
        doc.text(`Materialkosten gesamt: ${totalMaterialCost.toFixed(2)} €`, 14, yPos);
        yPos += 8;
      }

      // Arbeitskarte Section: Abschlusskontrolle
      const arbeitskarte = order.arbeitskarte;
      if (arbeitskarte) {
        if (yPos > 230) {
          doc.addPage();
          yPos = 20;
        }

        doc.setDrawColor(200);
        doc.line(14, yPos, pageWidth - 14, yPos);
        yPos += 10;

        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0);
        doc.text("ABSCHLUSSKONTROLLE", 14, yPos);
        yPos += 10;

        const kontrolle = arbeitskarte.abschlusskontrolle || {};
        const checkItems = [
          { label: "Alle Arbeiten abgeschlossen", checked: kontrolle.alle_arbeiten_abgeschlossen },
          { label: "Probefahrt durchgeführt", checked: kontrolle.probefahrt_durchgefuehrt },
          { label: "Funktionsprüfung elektrisch", checked: kontrolle.funktionspruefung_elektrisch },
        ];

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        checkItems.forEach((item) => {
          const box = item.checked ? "[X]" : "[  ]";
          doc.text(`${box}  ${item.label}`, 18, yPos);
          yPos += 7;
        });

        // Bemerkungen
        if (arbeitskarte.bemerkungen) {
          yPos += 5;
          doc.setFontSize(12);
          doc.setFont("helvetica", "bold");
          doc.text("BEMERKUNGEN", 14, yPos);
          yPos += 8;
          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          const splitBem = doc.splitTextToSize(arbeitskarte.bemerkungen, pageWidth - 28);
          doc.text(splitBem, 14, yPos);
          yPos += splitBem.length * 5 + 5;
        }
      }

      // Signature Section
      if (yPos > 230) {
        doc.addPage();
        yPos = 20;
      }

      yPos += 10;
      doc.setDrawColor(200);
      doc.line(14, yPos, pageWidth - 14, yPos);
      yPos += 15;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100);

      const sigLineWidth = 70;
      const leftX = 14;
      const rightX = pageWidth - 14 - sigLineWidth;

      // Date line
      doc.text("Datum: ____________________", leftX, yPos);
      yPos += 20;

      // Signature lines
      doc.setDrawColor(0);
      doc.line(leftX, yPos, leftX + sigLineWidth, yPos);
      doc.line(rightX, yPos, rightX + sigLineWidth, yPos);
      yPos += 5;
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text("Unterschrift Werkstatt", leftX, yPos);
      doc.text("Unterschrift Kunde", rightX, yPos);

      // Images Section (if requested and images exist)
      if (includeImages && orderImages.length > 0) {
        // Start images on a new page
        doc.addPage();
        yPos = 20;

        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0);
        doc.text("FOTODOKUMENTATION", 14, yPos);
        yPos += 10;

        // Group images by category
        const imageCategories = [
          { id: "fahrzeugannahme", label: "Fahrzeugannahme" },
          { id: "schaeden", label: "Schäden" },
          { id: "waehrend_arbeit", label: "Während der Arbeit" },
          { id: "fertigstellung", label: "Fertigstellung" },
          { id: "sonstiges", label: "Sonstiges" }
        ];

        // 1 image per row at near-full page width; preserve original aspect ratio,
        // so no distortion regardless of portrait/landscape photos.
        const startX = 14;
        const usableWidth = pageWidth - 2 * startX; // ≈ 182 mm on A4
        const slotWidth = usableWidth;
        const slotHeight = 110; // mm – two images per page comfortably
        const imageSpacing = 8;
        const descriptionGap = 6;
        const rowHeight = slotHeight + descriptionGap + imageSpacing + 4;
        const pageBottom = doc.internal.pageSize.getHeight() - 20;

        // Resize + JPEG-compress an image to keep PDF size small.
        // Returns { dataUrl, width, height } so we can render with correct aspect ratio.
        const loadCompressedImage = async (url) => {
          const response = await fetch(url, { mode: "cors" });
          const blob = await response.blob();
          const dataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          return await new Promise((resolve, reject) => {
            const im = new Image();
            im.onload = () => {
              const MAX = 1600;
              let w = im.naturalWidth, h = im.naturalHeight;
              if (Math.max(w, h) > MAX) {
                const scale = MAX / Math.max(w, h);
                w = Math.round(w * scale);
                h = Math.round(h * scale);
              }
              const canvas = document.createElement("canvas");
              canvas.width = w;
              canvas.height = h;
              const ctx = canvas.getContext("2d");
              ctx.fillStyle = "#ffffff";
              ctx.fillRect(0, 0, w, h);
              ctx.drawImage(im, 0, 0, w, h);
              resolve({
                dataUrl: canvas.toDataURL("image/jpeg", 0.82),
                width: w,
                height: h,
              });
            };
            im.onerror = reject;
            im.src = dataUrl;
          });
        };

        for (const category of imageCategories) {
          const categoryImages = orderImages.filter(img => img.category === category.id);
          if (categoryImages.length === 0) continue;

          // Need room for category header + at least one image
          if (yPos > pageBottom - slotHeight - 14) {
            doc.addPage();
            yPos = 20;
          }

          // Category header
          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(100);
          doc.text(category.label.toUpperCase(), startX, yPos);
          yPos += 8;

          for (const img of categoryImages) {
            // Wrap to next page if needed
            if (yPos + slotHeight > pageBottom) {
              doc.addPage();
              yPos = 20;
            }

            try {
              const imgUrl = img.url?.startsWith('http')
                ? img.url
                : `${process.env.REACT_APP_BACKEND_URL}${img.url}`;
              const compressed = await loadCompressedImage(imgUrl);

              // Fit-contain inside the slot, preserving original aspect ratio
              const ratio = compressed.width / compressed.height;
              const slotRatio = slotWidth / slotHeight;
              let drawW, drawH;
              if (ratio > slotRatio) {
                drawW = slotWidth;
                drawH = slotWidth / ratio;
              } else {
                drawH = slotHeight;
                drawW = slotHeight * ratio;
              }
              const drawX = startX + (slotWidth - drawW) / 2;
              const drawY = yPos + (slotHeight - drawH) / 2;
              doc.addImage(compressed.dataUrl, 'JPEG', drawX, drawY, drawW, drawH, undefined, 'FAST');

              // Description below the slot
              if (img.description) {
                doc.setFontSize(9);
                doc.setFont("helvetica", "normal");
                doc.setTextColor(80);
                const lines = doc.splitTextToSize(img.description, slotWidth);
                doc.text(lines.slice(0, 2), startX, yPos + slotHeight + 5);
              }
            } catch (e) {
              console.error("Error adding image to PDF:", e);
              doc.setDrawColor(200);
              doc.rect(startX, yPos, slotWidth, slotHeight);
              doc.setFontSize(9);
              doc.text("Bild konnte nicht geladen werden", startX + 2, yPos + slotHeight / 2);
            }

            yPos += rowHeight;
          }

          yPos += 2; // gap between categories
        }
      }

      // Footer on all pages
      const totalPages = doc.internal.getNumberOfPages();
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        const footerY = doc.internal.pageSize.getHeight() - 10;
        doc.setDrawColor(200);
        doc.line(14, footerY - 5, pageWidth - 14, footerY - 5);
        doc.setFontSize(7);
        doc.setTextColor(150);
        doc.text(`${companyName} | Generiert am ${new Date().toLocaleDateString("de-DE")}`, 14, footerY);
        doc.text(`Seite ${p} von ${totalPages}`, pageWidth - 14, footerY, { align: "right" });
      }

      // Save PDF
      const fileName = `Arbeitskarte_${order.auftragsnummer || order.id.slice(0, 8)}_${new Date().toISOString().slice(0, 10)}.pdf`;
      doc.save(fileName);
      
      toast.success("PDF erfolgreich erstellt!");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Fehler beim Erstellen des PDFs");
    } finally {
      setGenerating(false);
    }
  };

  const selectedOrder = getSelectedOrder();
  const selectedCustomer = selectedOrder ? getCustomer(selectedOrder.customer_id) : null;
  const selectedVehicle = selectedOrder ? getVehicle(selectedOrder.vehicle_id) : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="pdf-export-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
          Arbeitskarte PDF
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Arbeitskarte als professionelles PDF-Dokument exportieren
        </p>
      </div>

      {/* Order Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ClipboardList className="w-5 h-5" />
            Auftrag auswählen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Select
            value={selectedOrderId}
            onValueChange={setSelectedOrderId}
          >
            <SelectTrigger className="w-full" data-testid="order-select">
              <SelectValue placeholder="Auftrag auswählen..." />
            </SelectTrigger>
            <SelectContent>
              {orders.map((order) => {
                const customer = getCustomer(order.customer_id);
                const vehicle = getVehicle(order.vehicle_id);
                return (
                  <SelectItem key={order.id} value={order.id}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {order.auftragsnummer || order.id.slice(0, 8)}
                      </span>
                      <span className="text-muted-foreground">-</span>
                      <span>{customer?.name || "Unbekannt"}</span>
                      <span className="text-muted-foreground">
                        ({vehicle?.license_plate || order.service_object || "?"})
                      </span>
                      <Badge variant={order.status === 'erledigt' ? 'default' : 'secondary'} className="ml-2">
                        {getStatusLabel(order.status)}
                      </Badge>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Preview */}
      {selectedOrder && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Vorschau
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Order Info */}
            <div className="flex flex-wrap items-center gap-4 pb-4 border-b">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Auftragsnummer</p>
                <p className="font-bold text-lg">{selectedOrder.auftragsnummer || selectedOrder.id.slice(0, 8)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase">Status</p>
                <Badge className={
                  selectedOrder.status === 'erledigt' ? 'bg-green-100 text-green-800' :
                  selectedOrder.status === 'in_bearbeitung' ? 'bg-orange-100 text-orange-800' :
                  'bg-blue-100 text-blue-800'
                }>
                  {getStatusLabel(selectedOrder.status)}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase">Erstellt am</p>
                <p className="font-medium">{new Date(selectedOrder.created_at).toLocaleDateString("de-DE")}</p>
              </div>
            </div>

            {/* Customer & Vehicle */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Customer */}
              <div className="space-y-2">
                <h3 className="font-semibold flex items-center gap-2 text-sm">
                  <Users className="w-4 h-4" />
                  Kundendaten
                </h3>
                {selectedCustomer && (
                  <div className="text-sm space-y-1 pl-6">
                    <p><span className="text-muted-foreground">Name:</span> {selectedCustomer.name}</p>
                    <p><span className="text-muted-foreground">Telefon:</span> {selectedCustomer.phone || '-'}</p>
                    <p><span className="text-muted-foreground">E-Mail:</span> {selectedCustomer.email || '-'}</p>
                    <p><span className="text-muted-foreground">Adresse:</span> {[selectedCustomer.street, `${selectedCustomer.postal_code || ''} ${selectedCustomer.city || ''}`.trim()].filter(Boolean).join(', ') || '-'}</p>
                  </div>
                )}
              </div>

              {/* Vehicle */}
              <div className="space-y-2">
                <h3 className="font-semibold flex items-center gap-2 text-sm">
                  <Car className="w-4 h-4" />
                  Fahrzeugdaten
                </h3>
                {selectedVehicle && (
                  <div className="text-sm space-y-1 pl-6">
                    <p><span className="text-muted-foreground">Kennzeichen:</span> {selectedVehicle.license_plate}</p>
                    <p><span className="text-muted-foreground">Fahrzeug:</span> {selectedVehicle.brand} {selectedVehicle.model}</p>
                    <p><span className="text-muted-foreground">Baujahr:</span> {selectedVehicle.year || '-'}</p>
                    <p><span className="text-muted-foreground">VIN:</span> {selectedVehicle.vin || '-'}</p>
                    <p><span className="text-muted-foreground">Kilometerstand:</span> {selectedVehicle.mileage ? `${selectedVehicle.mileage.toLocaleString('de-DE')} km` : '-'}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Tasks Summary */}
            <div className="space-y-2">
              <h3 className="font-semibold flex items-center gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4" />
                Aufgaben ({selectedOrder.tasks?.filter(t => t.completed).length || 0}/{selectedOrder.tasks?.length || 0} erledigt)
              </h3>
              <div className="space-y-2 pl-6">
                {selectedOrder.tasks?.map((task, idx) => (
                  <div key={task.id || idx} className="flex items-start gap-2 text-sm">
                    <span className={task.completed ? "text-green-600" : "text-muted-foreground"}>
                      {task.completed ? "✓" : "○"}
                    </span>
                    <div>
                      <p className={task.completed ? "line-through text-muted-foreground" : ""}>
                        {task.description}
                      </p>
                      {task.materialien && task.materialien.length > 0 && (
                        <div className="mt-1 space-y-0.5">
                          {task.materialien.map((m, mi) => (
                            <p key={mi} className="text-xs text-muted-foreground pl-2 border-l-2 border-muted">
                              {m.bezeichnung}
                              {(m.anzahl || m.menge) ? ` | ${m.anzahl || m.menge} ${m.einheit || ''}` : ''}
                              {m.artikelnummer ? ` | Art.Nr. ${m.artikelnummer}` : ''}
                              {m.kosten > 0 ? ` | ${Number(m.kosten).toFixed(2)} €` : ''}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {(!selectedOrder.tasks || selectedOrder.tasks.length === 0) && (
                  <p className="text-sm text-muted-foreground italic">Keine Aufgaben</p>
                )}
              </div>
            </div>

            {/* Time Tracking Summary */}
            <div className="space-y-2">
              <h3 className="font-semibold flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4" />
                Zeiterfassung
              </h3>
              <div className="pl-6">
                {selectedOrder.total_work_minutes > 0 ? (
                  <p className="text-sm">
                    <span className="text-muted-foreground">Gesamtarbeitszeit:</span>{' '}
                    <span className="font-medium">{formatMinutes(selectedOrder.total_work_minutes)}</span>
                    {selectedOrder.time_entries && (
                      <span className="text-muted-foreground ml-2">
                        ({selectedOrder.time_entries.filter(e => !e.is_active && e.end_time).length} Einträge)
                      </span>
                    )}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground italic">Keine Zeiteinträge</p>
                )}
              </div>
            </div>

            {/* Material Costs */}
            <div className="space-y-2">
              <h3 className="font-semibold flex items-center gap-2 text-sm">
                <Package className="w-4 h-4" />
                Kosten-Übersicht
              </h3>
              <div className="pl-6">
                {(() => {
                  const totalMaterial = (selectedOrder.tasks || []).reduce((sum, task) => {
                    if (task.materialien && task.materialien.length > 0) {
                      return sum + task.materialien.reduce((mSum, m) => mSum + (m.kosten || 0), 0);
                    }
                    return sum + (task.material_cost || 0);
                  }, 0);
                  const TAX = parseFloat(companySettings?.tax_rate != null ? companySettings.tax_rate : 19);
                  const stundenlohn = parseFloat(companySettings?.stundenlohn || 0);
                  const workedMin = parseInt(selectedOrder.total_work_minutes || 0);
                  const laborNet = (workedMin / 60) * stundenlohn;
                  const netSum = totalMaterial + laborNet;
                  const taxSum = (netSum * TAX) / 100;
                  const grossSum = netSum + taxSum;
                  return (
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between max-w-sm">
                        <span className="text-muted-foreground">Material (netto):</span>
                        <span className="font-medium">{totalMaterial.toFixed(2)} €</span>
                      </div>
                      {laborNet > 0 && (
                        <div className="flex justify-between max-w-sm">
                          <span className="text-muted-foreground">Arbeit (netto):</span>
                          <span className="font-medium">{laborNet.toFixed(2)} €</span>
                        </div>
                      )}
                      <div className="flex justify-between max-w-sm">
                        <span className="text-muted-foreground">MwSt ({TAX}%):</span>
                        <span className="font-medium">{taxSum.toFixed(2)} €</span>
                      </div>
                      <div className="flex justify-between max-w-sm border-t pt-1 mt-1">
                        <span className="font-semibold">Gesamt (brutto):</span>
                        <span className="font-bold text-primary">{grossSum.toFixed(2)} €</span>
                      </div>
                      {stundenlohn === 0 && workedMin > 0 && (
                        <p className="text-xs text-amber-600 mt-2">
                          Tipp: Hinterlegen Sie einen Stundensatz in den Firmeneinstellungen, um Arbeitskosten automatisch zu berechnen.
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Photos Info */}
            {orderImages.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-semibold flex items-center gap-2 text-sm">
                  <ImageIcon className="w-4 h-4" />
                  Fotos ({orderImages.length})
                </h3>
                <div className="pl-6">
                  <div className="grid grid-cols-6 sm:grid-cols-8 gap-1">
                    {orderImages.slice(0, 8).map((img) => {
                      const imgUrl = img.url?.startsWith('http') 
                        ? img.url 
                        : `${process.env.REACT_APP_BACKEND_URL}${img.url}`;
                      return (
                      <img
                        key={img.id}
                        src={imgUrl}
                        alt={img.description || img.original_filename}
                        className="w-8 h-8 object-cover rounded"
                      />
                      );
                    })}
                    {orderImages.length > 8 && (
                      <div className="w-8 h-8 bg-muted rounded flex items-center justify-center text-xs text-muted-foreground">
                        +{orderImages.length - 8}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Export Buttons */}
            <div className="pt-4 border-t space-y-3">
              {/* Standard PDF buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => generatePdf(selectedOrder, false)}
                  disabled={generating}
                  className="btn-primary gap-2"
                  data-testid="export-pdf-button"
                >
                  {generating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Wird erstellt...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Arbeitskarte herunterladen
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => generatePdf(selectedOrder, false)}
                  disabled={generating}
                  className="gap-2"
                >
                  <Printer className="w-4 h-4" />
                  Drucken
                </Button>
              </div>

              {/* PDF with images buttons (only if images exist) */}
              {orderImages.length > 0 && (
                <div className="flex flex-col sm:flex-row gap-3 pt-3 border-t">
                  <Button
                    onClick={() => generatePdf(selectedOrder, true)}
                    disabled={generating}
                    variant="secondary"
                    className="gap-2"
                    data-testid="export-pdf-with-images-button"
                  >
                    {generating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                        Wird erstellt...
                      </>
                    ) : (
                      <>
                        <ImageIcon className="w-4 h-4" />
                        Arbeitskarte mit Fotos
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => generatePdf(selectedOrder, true)}
                    disabled={generating}
                    className="gap-2"
                  >
                    <Printer className="w-4 h-4" />
                    Drucken mit Bildern
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!selectedOrder && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <FileText className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg mb-2">Kein Auftrag ausgewählt</h3>
            <p className="text-muted-foreground text-sm">
              Wählen Sie einen Auftrag aus der Liste oben, um eine Vorschau zu sehen und ein PDF zu erstellen.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PdfExportPage;
